export { hasRole, hasAnyRole, getUserPermissions } from "@shared/roleHierarchy";
export type { RolePermissions } from "@shared/roleHierarchy";

import { getUserPermissions, type RolePermissions } from "@shared/roleHierarchy";

export function hasPermission(user: any, permission: keyof RolePermissions): boolean {
  const permissions = getUserPermissions(user);
  return permissions[permission];
}

export function requirePermission(user: any, permission: keyof RolePermissions): void {
  if (!hasPermission(user, permission)) {
    throw new Error(`Access denied: Missing permission ${permission}`);
  }
}

// Navigation menu helper - determines which menu items to show
export function getVisibleMenuItems(user: any) {
  const permissions = getUserPermissions(user);
  
  return {
    // Core Features (always visible to authenticated users)
    dashboard: true,
    assessments: true,
    customers: permissions.canViewCustomers,
    
    // Resources
    qbrs: permissions.canViewQbrs,
    templates: permissions.canViewTemplates,
    sops: permissions.canViewSops,
    
    // Administration
    userManagement: permissions.canViewUsers,
    settings: permissions.canAccessSettings,
    integrations: permissions.canViewIntegrations,
    
    // Activity (always visible)
    activity: true,
  };
}

// Role-based UI component visibility
export function shouldShowComponent(user: any, component: string): boolean {
  const permissions = getUserPermissions(user);
  
  switch (component) {
    // Customer management buttons
    case 'createCustomer':
      return permissions.canCreateCustomers;
    case 'editCustomer':
      return permissions.canEditCustomers;
    case 'deleteCustomer':
      return permissions.canDeleteCustomers;
    
    // Assessment management buttons
    case 'createAssessment':
      return permissions.canCreateAssessments;
    case 'deleteAssessment':
      return permissions.canDeleteAssessments;
    case 'approveAssessment':
      return permissions.canApproveAssessments;
    case 'reassignAssessment':
      return permissions.canReassignAssessments;
    
    // Template management buttons
    case 'createTemplate':
      return permissions.canCreateTemplates;
    case 'editTemplate':
      return permissions.canEditTemplates;
    case 'deleteTemplate':
      return permissions.canDeleteTemplates;
    
    // QBR management buttons
    case 'createQbr':
      return permissions.canCreateQbrs;
    case 'editQbr':
      return permissions.canEditQbrs;
    case 'deleteQbr':
      return permissions.canDeleteQbrs;
    
    // SOP management buttons
    case 'createSop':
      return permissions.canCreateSops;
    case 'editSop':
      return permissions.canEditSops;
    case 'deleteSop':
      return permissions.canDeleteSops;
    
    // User management buttons
    case 'createUser':
      return permissions.canCreateUsers;
    case 'editUser':
      return permissions.canEditUsers;
    case 'deleteUser':
      return permissions.canDeleteUsers;
    case 'manageRoles':
      return permissions.canManageRoles;
    
    // Integration and backup management
    case 'manageIntegrations':
      return permissions.canManageIntegrations;
    case 'manageBackups':
      return permissions.canManageBackups;
    
    default:
      return true; // Default to visible if component not found
  }
}

// Role display helpers
export function getRoleDisplayName(role: string): string {
  switch (role) {
    case 'admin':
      return 'Administrator';
    case 'vcio':
      return 'vCIO';
    case 'alignment_engineer':
      return 'Alignment Engineer';
    default:
      return role;
  }
}

export function getRoleDescription(role: string): string {
  switch (role) {
    case 'admin':
      return 'Full system access, user management, and configuration';
    case 'vcio':
      return 'Review and approve assessments, generate Business Reviews, manage customers';
    case 'alignment_engineer':
      return 'Create and complete assessments, view reports';
    default:
      return 'Basic user access';
  }
}

export function getRoleColor(role: string): string {
  switch (role) {
    case 'admin':
      return 'red';
    case 'vcio':
      return 'blue';
    case 'alignment_engineer':
      return 'green';
    default:
      return 'gray';
  }
}
